import React from 'react';
import {
  FDPaper, FDListItemText, FDListItem, FDListItemSecondary, FDPrimaryActionIconButton,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { pick } from 'Helpers/Affordances';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { advisorWhatIfPageUri, taskBoardUri } from 'Consts/advisorUris';
import { buildPageLink } from 'Views/Page/Content/Templates';
import { advisorPage, advisorPageMessages } from 'Consts/Strings';
import moment from 'moment-timezone';
import { missionControlURL } from 'Hooks/useMissionControlURL';

const HouseholdCurrentState = ({ resource }) => {
  const { pageContentResource } = usePageContentContext();

  const buildMeetingHref = (navigateRelationship) => {
    return missionControlURL(pageContentResource, pageContentResource.id(), {
      meetingSelfLink: navigateRelationship.link('related').url({
        params: {
          tz: moment.tz.guess(),
        },
      }),
    });
  };

  const buildMessageHref = (navigateRelationship) => {
    // attempt to pull off thread query param
    const params = resource?.withRelationship(
      fr.relationships.navigateSelectedItem.name,
      (rel) => ({ threadSelfLink: rel.link('related').url() }),
      () => {
      },
    );
    return buildPageLink(navigateRelationship.link('related'), advisorPageMessages, params);
  };

  const buildWhatIfHref = (navigateRelationship) => {
    return advisorWhatIfPageUri(navigateRelationship.link('related').url(), resource.attr('householdId'));
  };

  const buildActionItemHref = (navigateRelationship) => {
    return taskBoardUri({
      link: navigateRelationship.link('related').url(),
    });
  };

  const buildHref = () => {
    const navigateRelationship = resource?.relationship(fr.relationships.navigate.name);
    if (!navigateRelationship) {
      return {
        onClick: null,
        href: null,
      };
    }

    // Due to tech debt, navigating to a feature can take multiple forms.  Using the resource type, determine how to navigate to the feature.
    switch (navigateRelationship?.meta('resourceType')) {
      case 'meeting': {
        return buildMeetingHref(navigateRelationship);
      }
      case 'message': {
        return buildMessageHref(navigateRelationship);
      }
      case 'FacetPlanView': {
        return buildWhatIfHref(navigateRelationship);
      }
      case 'FacetPlanProposalPreview': {
        return buildWhatIfHref(navigateRelationship);
      }
      case 'simpleActionItem': {
        return buildActionItemHref(navigateRelationship);
      }
      default:
        return buildPageLink(navigateRelationship.link('related'), advisorPage);
    }
  };

  const href = buildHref();

  const action = pick(resource, {
    [fr.relationships.navigate.name]: (
      <FDListItemSecondary>
        <FDPrimaryActionIconButton icon={'chevronRight'} href={href} />
      </FDListItemSecondary>
    ),
  }, null);

  return (
    <FDPaper margin={200}>
      <FDListItem>
        <FDListItemText primaryText={resource.attr('description')} secondaryText={resource.attr('subtitle')} />
        {action}
      </FDListItem>
    </FDPaper>
  );
};

HouseholdCurrentState.defaultProps = {};

HouseholdCurrentState.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default HouseholdCurrentState;
